import { useQuery } from "@tanstack/react-query";
import type { QueueConfigSummary, QueueConfigsListResponse } from "@situate/contracts/runtime";
import { queueConfigsListResponseSchema } from "@situate/contracts/runtime-zod";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";

function queueConfigsPath(areaId: string): string {
  return `/v1/areas/${encodeURIComponent(areaId)}/queues`;
}

/** Loads the selected area's queue definitions, including their entrance and exit line references. */
export function useLiveQueueConfigs(deploymentId: string, areaId: string | null) {
  const runtime = useRuntimeHttpClient();
  const client = runtime.client;
  const hasGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "queue_configs_list"
  );
  const enabled =
    client !== null && areaId !== null && runtime.deploymentId === deploymentId && hasGrant;

  const query = useQuery({
    queryKey: ["queue-configs", deploymentId, areaId],
    queryFn: async (): Promise<QueueConfigsListResponse> => {
      if (client === null || areaId === null) {
        throw new Error("Queue configurations require a selected area and runtime client");
      }
      const response = await client.get<unknown>(queueConfigsPath(areaId));
      return queueConfigsListResponseSchema.parse(response);
    },
    enabled,
  });

  return {
    queueConfigs:
      query.data?.queues.filter((queue) => queue.enabled) ?? ([] as QueueConfigSummary[]),
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
  };
}
